<template>
  <div class="editor-toolbar">
    <v-sheet
      class="d-flex justify-space-between toolbar-content pl-2 pr-2"
    >
      <div class="left-bar d-flex">
        <TypeSelection
          v-if="!hideButton.typeSelect"
          :selected="nodeType"
          :items="nodeTypes"
          :disabled="disableAllButtons || disableButton.typeSelect"
          @button-click="$emit('buttonClick', $event)"
        />

        <div
          v-if="showFirstDivider"
          class="vertical-divider ml-2 mr-2 mt-3"
        />

        <ToolbarButtonGroup
          :buttons="nodeButtons"
          @button-click="$emit('buttonClick', $event)"
        />

        <div
          v-if="showSecondDivider"
          class="vertical-divider ml-2 mr-2 mt-3"
        />

        <ToolbarButtonGroup
          :buttons="formatButtons"
          @button-click="$emit('buttonClick', $event)"
        />

        <div
          v-if="showThirdDivider"
          class="vertical-divider ml-2 mr-2 mt-3"
        />

        <ToolbarButtonGroup
          v-if="showComments"
          class="comment-buttons"
          :buttons="commentButtons"
          @button-click="$emit('commentClick', $event)"
        />
      </div>

      <div class="right-bar d-flex">
        <div>
          <slot name="more" />
        </div>
        <ToolbarButtonGroup
          :buttons="historyButtons"
          @button-click="$emit('buttonClick', $event)"
        />
      </div>
    </v-sheet>
    <EditLinkModal @update-link="$emit('updateLink', $event)" />
  </div>
</template>

<script>
import ToolbarButtonGroup from './ToolbarButtonGroup.vue';
import TypeSelection from './TypeSelection.vue';
import EditLinkModal from './EditLinkModal.vue';

export default {
  name: 'EditorToolbar',
  components: { TypeSelection, ToolbarButtonGroup, EditLinkModal },
  props: {
    nodeType: { type: String, default: 'paragraph' },
    hideButton: { type: Object, default: () => ({}) },
    buttonState: { type: Object, default: () => ({}) },
    disableButton: { type: Object, default: () => ({}) },
    disableAllButtons: { type: Boolean, default: false },
    showComments: { type: Boolean, default: false },
  },
  emits: ['buttonClick', 'commentClick', 'updateLink'],

  computed: {
    formatButtons() {
      return [
        ['bold', 'Icon_Bold', 'Bold', ['Ctrl+B', 'Cmd+B']],
        ['italic', 'Icon_Italic', 'Italic', ['Ctrl+I', 'Cmd+I']],
        ['link', 'Icon_Link', 'Link', ['Ctrl+K', 'Cmd+K']],
        ['clear', 'Icon_Remove', 'Clear format', []],
      ]
        .filter(([id]) => !this.hideButton[id])
        .map(([id, icon, title, shortcuts]) => [
          id,
          icon,
          title,
          shortcuts,
          this.buttonState[id],
          this.disableAllButtons || this.disableButton[id],
        ]);
    },

    nodeButtons() {
      return [
        ['h1', 'Icon_Heading_H1', 'Heading 1', ['Ctrl+Alt+1', 'Cmd+Alt+1']],
        ['h2', 'Icon_Heading_H2', 'Heading 2', ['Ctrl+Alt+2', 'Cmd+Alt+2']],
        ['h3', 'Icon_Heading_H3', 'Heading 3', ['Ctrl+Alt+3', 'Cmd+Alt+3']],
        ['bulletList', 'Icon_List', 'Bullet list', ['Ctrl+Shift+8', 'Cmd+Shift+8']],
        ['orderedList', 'Icon_List_Numbered', 'Ordered list', ['Ctrl+Shift+7', 'Cmd+Shift+7']],
        ['indent', 'Icon_List_Indent', 'Indent', ['Tab']],
        ['unindent', 'Icon_List_Unindent', 'Unindent', ['Shift+Tab']],
      ]
        .filter(([id]) => !this.hideButton[id])
        .map(([id, icon, title, shortcuts]) => [
          id,
          icon,
          title,
          shortcuts,
          this.buttonState[id],
          this.disableAllButtons || this.disableButton[id],
        ]);
    },

    historyButtons() {
      return [
        ['undo', 'Icon_Undo', 'Undo', ['Ctrl+Z', 'Cmd+Z']],
        ['redo', 'Icon_Redo', 'Redo', ['Shift+Ctrl+Z', 'Shift+Cmd+Z', 'Ctrl+Y', 'Cmd+Y']],
      ]
        .filter(([id]) => !this.hideButton[id])
        .map(([id, icon, title, shortcuts]) => [
          id,
          icon,
          title,
          shortcuts,
          this.buttonState[id],
          this.disableAllButtons || this.disableButton[id],
        ]);
    },

    commentButtons() {
      return [
        ['comment', 'Icon_Comment', 'Comment', []],
      ]
        .filter(([id]) => !this.hideButton[id])
        .map(([id, icon, title, shortcuts]) => [
          id,
          icon,
          title,
          shortcuts,
          this.buttonState[id],
          this.disableButton[id],
        ]);
    },

    nodeTypes() {
      return [
        ['paragraph', 'Icon_Paragraph', 'Paragraph', 'Paragraph (Ctrl+Alt+0, Cmd+Alt+0)'],
        ['heading', 'Icon_Heading', 'Heading', 'Heading (Ctrl+Alt+1, Cmd+Alt+1)'],
        ['list', 'Icon_List', 'List', 'List (Ctrl+Shift+8, Cmd+Shift+8)'],
      ].filter(([id]) => !this.hideButton[id]);
    },

    showFirstDivider() {
      return !this.hideButton.typeSelect && this.nodeType !== 'list'
       && !(this.nodeType === 'paragraph' && this.formatButtons.length === 0);
    },

    showSecondDivider() {
      return this.nodeButtons.length > 0 && this.formatButtons.length > 0;
    },

    showThirdDivider() {
      return this.showComments && this.commentButtons.length > 0;
    },
  },
};
</script>

<style scoped lang="less">
@import '@/less/mixins';

.editor-toolbar {
  .toolbar-content {
    height: 48px;
  }

  .vertical-divider {
    height: 24px;
    border-left: fade(@text-darkest, 20%) solid 1px;
  }

  .comment-buttons :deep(svg) {
    height: 16px;
  }
}
</style>
